import type {
	BillSearchFilters,
	BillSortOrder,
} from "../../queries/bill.queries";
import type { SelectOption } from "../form-inputs/select-box.component";
import { formatCustomerIdParam } from "../../global/customer-id.util";

// The formik draft shape bill-filter.component.tsx edits — deliberately NOT
// the same shape as BillSearchFilters (the POST body bill/search expects).
// bill_for is the tag-input's string[], and total_sales_min/max are live
// numbers the range slider edits directly. transformBillFilters below is the
// one place that reshapes this into an actual search payload — on Search
// submit only, never on every keystroke (see invoice-bill.page.tsx: the query
// is driven by the last-submitted filters, not filterFormik.values live).
// Status (void/canceled/ird-sync) is no longer part of this form — it's
// driven by the header stat pills instead, see statusFilterToParams below.
export interface BillFilterFormValues {
	date_from: string;
	date_to: string;
	customer_id: string;
	buyer_name: string;
	buyer_pan: string;
	invoice_number: string;
	total_sales_min: number;
	total_sales_max: number;
	fiscal_year: string;
	bill_for: string[];
}

export const initialBillFilterValues: BillFilterFormValues = {
	date_from: "",
	date_to: "",
	customer_id: "",
	buyer_name: "",
	buyer_pan: "",
	invoice_number: "",
	total_sales_min: 0,
	total_sales_max: 0,
	fiscal_year: "",
	bill_for: [],
};

// Drives the header stat pills in bill-list.component.tsx — replaces the old
// single bill_void dropdown with a richer, single-select status filter that
// also exposes bill_canceled/ird_bill (both already valid BillSearchFilters
// the backend accepted but this form never populated). "all" clears every
// status axis. Mirrors error-fix.page.tsx's My/Team Bills tab pattern.
export type BillStatusFilter =
	| "all"
	| "bill"
	| "credit"
	| "failed"
	| "canceled";

export function statusFilterToParams(
	statusFilter: BillStatusFilter,
): Pick<BillSearchFilters, "bill_void" | "bill_canceled" | "ird_bill"> {
	switch (statusFilter) {
		case "bill":
			return { bill_void: false, bill_canceled: false, ird_bill: true };
		case "credit":
			return { bill_void: true };
		case "failed":
			return { ird_bill: false };
		case "canceled":
			return { bill_canceled: true };
		default:
			return {};
	}
}

// Drives the "Sort by" dropdown in bill-list.component.tsx's bill-date-info
// section — id is the friendly BillSortOrder BILL_SORT_MAP (bill.queries.ts)
// translates into the actual sort_by/sort_order bill/search expects.
export const BILL_SORT_OPTIONS: SelectOption[] = [
	{
		id: "invoice_number_desc" satisfies BillSortOrder,
		value: "Invoice Number (Newest)",
	},
	{
		id: "invoice_number_asc" satisfies BillSortOrder,
		value: "Invoice Number (Oldest)",
	},
	// { id: "invoice_date_desc" satisfies BillSortOrder, value: "Invoice Date (Newest)" },
	// { id: "invoice_date_asc" satisfies BillSortOrder, value: "Invoice Date (Oldest)" },
	// { id: "sales_date_desc" satisfies BillSortOrder, value: "Sales Date (Newest)" },
	// { id: "sales_date_asc" satisfies BillSortOrder, value: "Sales Date (Oldest)" },
	{
		id: "total_sales_desc" satisfies BillSortOrder,
		value: "Total Sales (Highest)",
	},
	{
		id: "total_sales_asc" satisfies BillSortOrder,
		value: "Total Sales (Lowest)",
	},
	{ id: "recent_added" satisfies BillSortOrder, value: "Recently Added" },
	{ id: "recent_updated" satisfies BillSortOrder, value: "Recently Updated" },
	{
		id: "last_print_date_desc" satisfies BillSortOrder,
		value: "Last Printed",
	},
	// { id: "last_print_date_asc" satisfies BillSortOrder, value: "Last Printed (Oldest)" },
];

// Mirrors the transform invoice-bill.page.jsx used to apply inline in its
// formik onSubmit before dispatching searchBills — extracted so both the
// Search-button submit path and the mount-time customerId-from-navigation
// seeding path (see invoice-bill.page.tsx) can share it instead of
// duplicating the reshape.
export function transformBillFilters(
	values: BillFilterFormValues,
): BillSearchFilters {
	return {
		buyer_name: values.buyer_name,
		buyer_pan: values.buyer_pan,
		invoice_number: values.invoice_number,
		total_sales_min: values.total_sales_min,
		total_sales_max: values.total_sales_max,
		fiscal_year: values.fiscal_year,
		date_from: values.fiscal_year === "" ? values.date_from : "",
		date_to: values.fiscal_year === "" ? values.date_to : "",
		customer_id: formatCustomerIdParam(values.customer_id),
		bill_for:
			Array.isArray(values.bill_for) && values.bill_for.length > 0
				? "#" + values.bill_for.join("#")
				: "",
	};
}
